import type {PerkCard as PerkCardType} from '@cohort/components-xps/components/cards/PerkCard';
import {defaultErrorMessage} from '@cohort/shared/models';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import TrackedPerkCard from '@cohort/wallet/components/card/TrackedPerkCard';
import EmptySection from '@cohort/wallet/components/space/EmptySection';
import {useGeneratePrivateContentUrl} from '@cohort/wallet/hooks/api/Ownerships';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useNotify from '@cohort/wallet/hooks/notify';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getPerkDetailRoute} from '@cohort/wallet/lib/Pages';
import type {AccessesAndOwnership} from '@cohort/wallet/lib/Utils';
import {getPerkIntancesAndOwnership} from '@cohort/wallet/lib/Utils';
import type {PerkWDto} from '@cohort/wallet-schemas/perk';
import type {PerkSpaceViewWDto} from '@cohort/wallet-schemas/perkAccess';
import {Gift} from '@phosphor-icons/react';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type PerksListDisplayMode = 'grid' | 'carousel';

type PerkMultiInstancesCardProps = {
  perk: PerkWDto;
  accessesAndOwnerships: AccessesAndOwnership[];
  displayMode: PerksListDisplayMode;
};

type PerksListProps = {
  perks: Array<PerkSpaceViewWDto>;
  displayMode: PerksListDisplayMode;
};

const PerkMultiInstancesCard: React.FC<PerkMultiInstancesCardProps> = ({
  perk,
  accessesAndOwnerships,
  displayMode,
}) => {
  const navigate = useNavigate();
  const {setTransitioning} = useSpaceContext();
  const {hasDarkBg, accentColor} = useThemeContext();
  const notify = useNotify();
  const trackingCallback = useClickTracker('perks.list.card', {
    ownershipId: accessesAndOwnerships[0]?.ownership.id,
    accessId: accessesAndOwnerships[0]?.access.id,
    digitalAssetId: accessesAndOwnerships[0]?.ownership.digitalAsset.id,
    perkId: perk.id,
  });
  const {isMobile} = useScreenSizeContext();

  const anyInstance = accessesAndOwnerships[0]?.access;

  if (anyInstance === undefined) {
    notify('error', defaultErrorMessage);
  }

  const usableInstances = accessesAndOwnerships.filter(({access}) => access.canBeUsed);
  const firstUsableInstanceAndOwnership =
    usableInstances.length > 0 ? usableInstances[0] : undefined;
  const disabled = firstUsableInstanceAndOwnership === undefined;

  const {data: privateContentUrlResponse} = useGeneratePrivateContentUrl(
    accessesAndOwnerships[0]?.ownership.id ?? '',
    accessesAndOwnerships[0]?.access.id ?? '',
    perk.privateContentFileType ?? '',
    accessesAndOwnerships[0] !== undefined &&
      perk.videoFileKey === null &&
      perk.privateContentFileType === 'video'
  );

  const privateContentVideoUrl =
    perk.privateContentFileType === 'video' ? privateContentUrlResponse?.url : null;

  const perkCard: PerkCardType = {
    perkVideoFileKey: perk.videoFileKey ?? privateContentVideoUrl ?? null,
    perkThumbnailFileKey: perk.thumbnailFileKey ?? perk.privateContentThumbnailFileKey ?? null,
    perkBannerFileKey: perk.bannerFileKey,
    perkDisplayName: perk.displayName,
    canBeUsed: !disabled,
    digitalAssetImageFileKey: accessesAndOwnerships[0]?.ownership.digitalAsset.imageFileKey ?? null,
    perkAccessCounter: accessesAndOwnerships.length,
    hasDarkBg,
    accentColor,
    handleAction: () => {
      trackingCallback();
      setTransitioning({active: true, enterFrom: 'left'});
      navigate(getPerkDetailRoute(perk.id));
    },
    raffleStatus: accessesAndOwnerships[0]?.access.raffleStatus,
    statusMessage: accessesAndOwnerships[0]?.access.statusMessage,
  };

  perkCard.hideMediaCover =
    displayMode === 'grid' &&
    isMobile &&
    perkCard.perkBannerFileKey === null &&
    perkCard.perkThumbnailFileKey === null;

  return (
    <TrackedPerkCard
      perkCard={perkCard}
      className={classNames(
        'flex',
        displayMode === 'carousel' && 'md:w-[var(--perk-card-min-width-desktop)]'
      )}
      metadata={{
        perkId: perk.id,
        ownershipId: accessesAndOwnerships[0]?.ownership.id,
        accessId: accessesAndOwnerships[0]?.access.id,
        digitalAssetId: accessesAndOwnerships[0]?.ownership.digitalAsset.id,
      }}
    />
  );
};

const PerksList: React.FC<PerksListProps> = ({perks, displayMode}) => {
  const {space} = useSpaceContext();
  const {hasDarkBg} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.perksList'});

  if (space === undefined) {
    return <Fragment />;
  }

  if (perks.length === 0) {
    return (
      <EmptySection
        icon={
          <Gift
            className={classNames(
              'block h-8 w-8 place-items-center',
              hasDarkBg ? 'text-white' : 'text-black'
            )}
            aria-hidden="true"
          />
        }
        title={t('emptyPerkListTitle')}
        subtitle={t('emptyPerkListSubtitle')}
      />
    );
  }

  return (
    <div
      className={classNames(
        'gap-5',
        displayMode === 'carousel'
          ? 'no-scrollbar -mx-4 flex overflow-auto px-4 md:mx-0 md:px-0'
          : 'grid grid-cols-1 lg:[grid-template-columns:repeat(auto-fill,minmax(var(--perk-card-min-width),1fr))]'
      )}
    >
      {perks.map(({perk, instances}) => (
        <PerkMultiInstancesCard
          key={perk.id}
          perk={perk}
          displayMode={displayMode}
          accessesAndOwnerships={getPerkIntancesAndOwnership(space, instances)}
        />
      ))}
    </div>
  );
};

export default PerksList;
